import React, { useCallback, useState } from 'react';
import Product from '../Types/Product';
import Icon from '../Components/Icon';
import { translate } from '../Services/translation';
import clsx from 'clsx';
import BuyButton from '../Components/BuyButton/BuyButton';
import ProductPrice from '../Components/ProductPrice/ProductPrice';
import ResponsiveImage from '../Components/ResponsiveImage/ResponsiveImage';
import { ResponsiveImageModel } from '../Types/ResponsiveImageModel';
import { useTypedSelector } from '../Hooks/useTypedSelector';
import {
  dataLayerBeginCheckoutUpsales,
  pushEcommerce,
} from '../Services/Gtm.service';

export interface UpsellBlockModel {
  products: Product[];
  title: string;
  initialShowItems?: boolean;
}

const getSmallestImage = (imageModel: ResponsiveImageModel) =>
  imageModel.sizeDefinitions?.sort((a, b) => a.width - b.width)[0]!;

const UpsellBlock = ({
  products,
  title,
  initialShowItems = true,
}: UpsellBlockModel) => {
  const { orderRows } = useTypedSelector((state) => state.cart);
  const [showItems, setShowItems] = useState(initialShowItems);

  const cartHasItem = useCallback(
    (articleNumber?: string) =>
      !!orderRows.find(
        (r) =>
          r.articleNumber?.toLowerCase() == articleNumber?.toLowerCase()
      ),
    [orderRows]
  );

  return products.length > 0 ? (
    <div className="mx-auto mb-6 w-full bg-white lg:max-w-[660px] lg:rounded">
      <button
        className="flex w-full flex-1 cursor-pointer flex-row items-center justify-between px-6 pb-5 pt-6"
        onClick={() => setShowItems(!showItems)}
      >
        <h3 className="flex flex-1 text-xl md:text-h3">
          {title ?? translate('upsell-block.fallback-title')}
        </h3>
        <Icon
          name="chevron-down"
          className={clsx(
            'transition-transform',
            showItems ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>
      <div
        className={clsx(
          'grid grid-rows-[0fr] border-t border-solid border-gray-100 transition-[grid-template-rows] duration-200',
          showItems ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-x-4 gap-y-5 px-4 py-6 lg:px-6">
            {products.map((p) => (
              <div className="flex w-full gap-4" key={'upsell-' + p.id}>
                {p.patternImage ||
                (p.images && p.images.length > 0 && p.images[0]) ? (
                  <a href={p.url}>
                    <img
                      src={
                        p.patternImage
                          ? getSmallestImage(p.patternImage).url
                          : p.images![0]?.thumbnailSrc
                      }
                      className={clsx(
                        'w-full max-w-[80px] rounded object-cover transition-opacity duration-200 aspect-[3/4]',
                        cartHasItem(p.id) && 'opacity-50'
                      )}
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <div></div>
                )}
                <div className="flex flex-grow flex-col gap-1">
                  <div
                    className={clsx(
                      'flex flex-1 flex-col gap-y-2 transition-opacity duration-200',
                      cartHasItem(p.id) && 'opacity-50'
                    )}
                  >
                    <div className="text-md leading-tight text-gray-500">
                      {`${p.productType}: ${p.id}`}
                    </div>
                    <div className="flex flex-col gap-4">
                      <a href={p.url}>
                        <h4 className="sm-down:text-lg">{p.name}</h4>
                      </a>
                      <div className="flex flex-col text-md font-normal leading-tight text-gray-500 lg:block">
                        {p.price.campaignPrice
                          ? p.price.formattedCampaignPrice
                          : p.price.formattedPrice}
                        {p.unitOfMeasurement &&
                          ' / ' + p.unitOfMeasurement.toLowerCase()}
                        {p.price.campaignPrice && (
                          <span className="mt-2 text-sm font-light text-gray-400 lg:ml-2 lg:mt-0">
                            {translate('cart-item.regular-unit-price')}{' '}
                            {p.price.formattedPrice}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {!p.disableBuyOption && (
                  <BuyButton
                    articleNumber={p.id}
                    quantity={1}
                    hideIcon={true}
                    btnSize="medium"
                    persistCheckmark={cartHasItem(p.id)}
                    onAddToCart={() =>
                      p.gtmEcommerceItem &&
                      dataLayerBeginCheckoutUpsales([p.gtmEcommerceItem])
                    }
                    disabled={
                      (p.stockStatus && !p.stockStatus.inStock) ||
                      !p.id ||
                      cartHasItem(p.id)
                    }
                    className="my-auto !w-auto"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default UpsellBlock;
